import { Modal, ActionIcon, Table } from '@mantine/core';
import { modals } from '@mantine/modals';
import React from 'react';
import Button from '../../common/components/Button';
import { hasTokens } from '../utils/tokenProcessor';
import { ALL_COLUMNS } from '../constants/ptoAllocationConstants';
import { OverrideRuleEditor } from './OverrideRuleEditor';

export const PTOOverrideModal = ({
  opened,
  onClose,
  overrides,
  setOverrides,
  unsavedOverrides,
  setUnsavedOverrides,
  hasUnsavedChanges,
  setHasUnsavedChanges,
  availableTokenFields,
  onSave,
}) => {
  // Helper to get label from ALL_COLUMNS
  const getColumnLabel = field => {
    const col = ALL_COLUMNS.find(col => col.value === field);
    return col ? col.label : field;
  };

  const moveOverride = (fromIndex, toIndex) => {
    setOverrides(prev => {
      if (toIndex < 0 || toIndex >= prev.length || fromIndex === toIndex) return prev;
      const next = [...prev];
      const [item] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, item);
      return next;
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={() => {
        if (hasUnsavedChanges) {
          if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
            setOverrides(unsavedOverrides);
            setHasUnsavedChanges(false);
            onClose();
          }
        } else {
          onClose();
        }
      }}
      title={
        <div className="flex items-center gap-2">
          <span>Field Overrides</span>
          {hasUnsavedChanges && (
            <span className="text-sm text-orange-600 font-normal">(unsaved changes)</span>
          )}
        </div>
      }
      size="xxl"
      yOffset="2vh"
      overlayProps={{ blur: 2 }}
      className="[&>div]:!p-8"
      styles={{
        content: {
          maxHeight: '90vh',
          overflowY: 'auto',
          minWidth: 'min(1100px, 98vw)',
          maxWidth: '98vw',
        },
      }}
    >
      <div className="flex justify-between mb-2 gap-2 ml-auto">
        <div className="flex flex-row w-full justify-between items-center gap-2">
          <div className="flex flex-row gap-2">
            <Button
              size="small"
              variant="outline"
              color="blue"
              onClick={() => {
                const data = JSON.stringify(overrides, null, 2);
                const blob = new Blob([data], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'timesheet-daily-snapshot-overrides.json';
                document.body.appendChild(a);
                a.click();
                setTimeout(() => {
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }, 0);
              }}
            >
              Export Rules
            </Button>
            <Button
              size="small"
              variant="outline"
              color="blue"
              onClick={() => document.getElementById('import-overrides-input').click()}
            >
              Import Rules
            </Button>
            <input
              id="import-overrides-input"
              type="file"
              accept="application/json"
              style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = event => {
                  try {
                    const imported = JSON.parse(event.target.result);
                    if (Array.isArray(imported)) {
                      if (
                        window.confirm(
                          'Are you sure you want to import and overwrite your current overrides?',
                        )
                      ) {
                        setOverrides(imported);
                      }
                    } else {
                      alert('Invalid format: JSON must be an array.');
                    }
                  } catch (err) {
                    alert('Failed to import: Invalid JSON.');
                  }
                };
                reader.readAsText(file);
                e.target.value = '';
              }}
            />
          </div>
          <div className="flex flex-row gap-2 ml-auto">
            <Button
              color="blue"
              variant="outline"
              onClick={() => {
                // Open Mantine modals manager modal for adding a new rule
                let tempConditions = [{ field: '', value: '' }];
                let tempOverrideFields = [{ field: '', value: '' }];
                modals.open({
                  title: 'Add New Override Rule',
                  size: 'lg',
                  children: (
                    <OverrideRuleEditor
                      initialConditions={tempConditions}
                      initialOverrideFields={tempOverrideFields}
                      availableTokenFields={availableTokenFields}
                      allColumns={ALL_COLUMNS}
                      onSave={(conditions, overrideFields) => {
                        const overrideObj = {};
                        overrideFields.forEach(o => {
                          if (o.field && o.value !== undefined && o.value !== '') {
                            overrideObj[o.field] = o.value;
                          }
                        });
                        setOverrides(prev => [
                          ...prev,
                          {
                            conditions: conditions.filter(c => c.field && c.value),
                            override: overrideObj,
                          },
                        ]);
                        modals.closeAll();
                      }}
                    />
                  ),
                });
              }}
            >
              Add
            </Button>
          </div>
        </div>
      </div>
      <div className="mt-2 relative">
        {overrides.length > 0 ? (
          <Table
            verticalSpacing="xs"
            horizontalSpacing="xs"
            fontSize="xs"
            className="min-w-full"
            withBorder
          >
            <thead>
              <tr className="bg-gray-200">
                <th className="rounded-tl whitespace-nowrap">Conditions</th>
                <th className="whitespace-nowrap">Overrides</th>
                <th className="rounded-tr text-right whitespace-nowrap"></th>
              </tr>
            </thead>
            <tbody>
              {overrides.map((ovr, ovrIdx) => (
                <tr
                  key={`ovr-${ovrIdx}`}
                  className="bg-white rounded border hover:bg-blue-50 transition"
                  style={{ boxShadow: 'none' }}
                >
                  <td className="text-gray-700 align-top whitespace-nowrap">
                    <div className="flex flex-col gap-0.5">
                      {ovr.conditions.map((c, condIdx) => (
                        <div key={condIdx}>
                          <span className="font-bold text-blue-900">
                            {getColumnLabel(c.field)}:
                          </span>{' '}
                          {c.value}
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="align-top whitespace-nowrap">
                    <div className="flex flex-col gap-0.5">
                      {Object.entries(ovr.override).map(([field, value], idx) => (
                        <div key={idx} className="whitespace-nowrap">
                          <span className="font-bold text-blue-900">
                            {getColumnLabel(field)}:
                          </span>{' '}
                          <span className={hasTokens(value) ? "text-green-700 font-mono" : "text-blue-700"}>
                            {value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="text-right align-top">
                    <div className="flex flex-row justify-end gap-1">
                      <ActionIcon
                        color="gray"
                        variant="subtle"
                        onClick={() => moveOverride(ovrIdx, ovrIdx - 1)}
                        disabled={ovrIdx === 0}
                        title="Move Up"
                        className="hover:bg-gray-100 hover:text-gray-700 transition mr-1"
                        style={{ minHeight: 28, minWidth: 28 }}
                      >
                        <span className="text-base font-bold">↑</span>
                      </ActionIcon>
                      <ActionIcon
                        color="gray"
                        variant="subtle"
                        onClick={() => moveOverride(ovrIdx, ovrIdx + 1)}
                        disabled={ovrIdx === overrides.length - 1}
                        title="Move Down"
                        className="hover:bg-gray-100 hover:text-gray-700 transition mr-1"
                        style={{ minHeight: 28, minWidth: 28 }}
                      >
                        <span className="text-base font-bold">↓</span>
                      </ActionIcon>
                      <ActionIcon
                        color="blue"
                        variant="subtle"
                        onClick={() => {
                          // Open modal to edit this rule
                          modals.open({
                            title: 'Edit Override Rule',
                            size: 'lg',
                            children: (
                              <OverrideRuleEditor
                                initialConditions={ovr.conditions.map(c => ({ ...c }))}
                                initialOverrideFields={Object.entries(ovr.override).map(
                                  ([field, value]) => ({ field, value }),
                                )}
                                availableTokenFields={availableTokenFields}
                                allColumns={ALL_COLUMNS}
                                onSave={(conditions, overrideFields) => {
                                  const overrideObj = {};
                                  overrideFields.forEach(o => {
                                    if (o.field && o.value !== undefined && o.value !== '') {
                                      overrideObj[o.field] = o.value;
                                    }
                                  });
                                  setOverrides(prev => [
                                    ...prev.slice(0, ovrIdx),
                                    {
                                      conditions: conditions.filter(c => c.field && c.value),
                                      override: overrideObj,
                                    },
                                    ...prev.slice(ovrIdx + 1),
                                  ]);
                                  modals.closeAll();
                                }}
                              />
                            ),
                          });
                        }}
                        title="Edit Override"
                        className="hover:bg-blue-100 hover:text-blue-700 transition mr-1"
                        style={{ minHeight: 28, minWidth: 28 }}
                      >
                        <span className="text-base font-bold">✎</span>
                      </ActionIcon>
                      <ActionIcon
                        color="gray"
                        variant="subtle"
                        onClick={() => {
                          // Duplicate rule and open edit modal for the new rule
                          const newConditions = ovr.conditions.map(c => ({ ...c }));
                          const newOverrideFields = Object.entries(ovr.override).map(
                            ([field, value]) => ({ field, value }),
                          );
                          // Insert duplicate after current
                          setOverrides(prev => {
                            const newRule = {
                              conditions: newConditions,
                              override: { ...ovr.override },
                            };
                            const newOverrides = [
                              ...prev.slice(0, ovrIdx + 1),
                              newRule,
                              ...prev.slice(ovrIdx + 1),
                            ];
                            // Open modal for the new rule after state update
                            setTimeout(() => {
                              modals.open({
                                title: 'Edit Duplicated Override Rule',
                                size: 'lg',
                                children: (
                                  <OverrideRuleEditor
                                    initialConditions={newConditions.map(c => ({ ...c }))}
                                    initialOverrideFields={newOverrideFields.map(f => ({
                                      ...f,
                                    }))}
                                    availableTokenFields={availableTokenFields}
                                    allColumns={ALL_COLUMNS}
                                    onSave={(conditions, overrideFields) => {
                                      const overrideObj = {};
                                      overrideFields.forEach(o => {
                                        if (
                                          o.field &&
                                          o.value !== undefined &&
                                          o.value !== ''
                                        ) {
                                          overrideObj[o.field] = o.value;
                                        }
                                      });
                                      setOverrides(current => {
                                        const idx = ovrIdx + 1;
                                        return [
                                          ...current.slice(0, idx),
                                          {
                                            conditions: conditions.filter(
                                              c => c.field && c.value,
                                            ),
                                            override: overrideObj,
                                          },
                                          ...current.slice(idx + 1),
                                        ];
                                      });
                                      modals.closeAll();
                                    }}
                                  />
                                ),
                              });
                            }, 0);
                            return newOverrides;
                          });
                        }}
                        title="Duplicate Override"
                        className="hover:bg-gray-100 hover:text-gray-700 transition mr-1"
                        style={{ minHeight: 28, minWidth: 28 }}
                      >
                        {/* Use the local Icon component for 'copy' */}
                        <span className="text-base font-bold">
                          {/* eslint-disable-next-line */}
                          {React.createElement(
                            require('../../common/components/Icon').default,
                            { type: 'copy', style: { fontSize: 16 } },
                          )}
                        </span>
                      </ActionIcon>
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        onClick={() => {
                          if (
                            window.confirm(
                              'Are you sure you want to delete this override rule?',
                            )
                          ) {
                            setOverrides(prev => prev.filter((_, i) => i !== ovrIdx));
                          }
                        }}
                        title="Remove Override"
                        className="hover:bg-red-100 hover:text-red-700 transition"
                        style={{ minHeight: 28, minWidth: 28 }}
                      >
                        <span className="text-base font-bold">×</span>
                      </ActionIcon>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <div className="text-gray-500 text-xs p-2">No overrides set.</div>
        )}
        {/* Sticky footer for Clear All and Save buttons */}
        <div
          className="flex flex-row justify-between gap-2 p-2 bg-white border-t sticky"
          style={{ bottom: 0, left: 0, right: 0, zIndex: 10 }}
        >
          <Button
            color="red"
            variant="outline"
            onClick={() => {
              if (window.confirm('Are you sure you want to clear all overrides?')) {
                setOverrides([]);
              }
            }}
          >
            Clear All
          </Button>
          <div className="flex gap-2">
            {hasUnsavedChanges && (
              <Button
                type="outline"
                onClick={() => {
                  setOverrides(unsavedOverrides);
                  setHasUnsavedChanges(false);
                }}
              >
                Revert Changes
              </Button>
            )}
            <Button
              type="primary"
              disabled={!hasUnsavedChanges}
              onClick={onSave}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
